import { useEffect, useState } from 'react';
import { logger } from '../utils/logger';
import { Crown, Check, Music, Download, Video, Star, Zap, Lock } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { prepareCheckoutWindow, completeCheckoutRedirect, closeCheckoutWindow, isStandalonePWA } from '../utils/pwaCheckout';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TIERS = {
  free: {
    name: 'Free',
    price: 0,
    features: [
      { text: 'Stream music (with limits)', included: true },
      { text: 'Purchase tracks & albums', included: true },
      { text: 'Create playlists', included: true },
      { text: 'Unlimited streaming', included: false },
      { text: 'Exclusive content', included: false },
      { text: 'Early access releases', included: false },
      { text: 'Download all tracks', included: false },
      { text: 'No ads', included: false },
    ],
    color: 'from-gray-600 to-gray-700',
    icon: Music,
  },
  premium: {
    name: 'Premium',
    price: 9.99,
    features: [
      { text: 'Stream music (with limits)', included: true },
      { text: 'Purchase tracks & albums', included: true },
      { text: 'Create playlists', included: true },
      { text: 'Unlimited streaming', included: true },
      { text: 'Exclusive content', included: true },
      { text: 'Early access releases', included: true },
      { text: 'Download all tracks', included: true },
      { text: 'No ads', included: true },
    ],
    color: 'from-purple-500 to-pink-500',
    icon: Crown,
  }
};

export default function SubscriptionPage() {
  const { isAuthenticated, user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      fetchSubscription();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const fetchSubscription = async () => {
    try {
      const res = await axios.get(`${API_URL}/subscription`);
      setSubscription(res.data);
    } catch (error) {
      logger.error('Failed to fetch subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (tier) => {
    if (!isAuthenticated) {
      toast.error('Please sign in to subscribe');
      return;
    }

    if (tier === 'free') {
      toast.info('You already have free access!');
      return;
    }

    setCheckoutLoading(true);
    const pwaWindow = prepareCheckoutWindow();
    try {
      const res = await axios.post(`${API_URL}/subscription/checkout`, {
        tier,
        origin_url: window.location.origin
      });

      if (res.data.checkout_url) {
        if (isStandalonePWA()) {
          toast.info('Opening secure checkout in your browser. Come back here when done.');
        }
        completeCheckoutRedirect(pwaWindow, res.data.checkout_url);
      } else {
        closeCheckoutWindow(pwaWindow);
      }
    } catch (error) {
      closeCheckoutWindow(pwaWindow);
      logger.error('Checkout error:', error);
      toast.error('Failed to start checkout. Please try again.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription?')) return;
    
    try {
      await axios.post(`${API_URL}/subscription/cancel`);
      toast.success('Subscription cancelled');
      fetchSubscription();
    } catch (error) {
      toast.error('Failed to cancel subscription');
    }
  };

  const isPremium = subscription?.tier === 'premium' && subscription?.status === 'active';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="main-content pt-24" data-testid="subscription-page">
      <div className="container py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mx-auto mb-4">
            <Crown className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold mb-4">Upgrade Your Experience</h1>
          <p className="text-gray-400 max-w-xl mx-auto">
            Get unlimited streaming, exclusive content, and more with a Premium subscription
          </p>
        </div>

        {/* Current Status */}
        {isAuthenticated && subscription && (
          <div className="glass-card p-6 mb-12 max-w-2xl mx-auto" data-testid="subscription-status">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${isPremium ? 'from-purple-500 to-pink-500' : 'from-gray-600 to-gray-700'} flex items-center justify-center`}>
                  {isPremium ? <Crown className="w-6 h-6 text-white" /> : <Music className="w-6 h-6 text-white" />}
                </div>
                <div>
                  <h3 className="font-semibold">Current Plan: {isPremium ? 'Premium' : 'Free'}</h3>
                  <p className="text-sm text-gray-400">
                    {isPremium 
                      ? `Active until ${new Date(subscription.current_period_end).toLocaleDateString()}`
                      : 'Upgrade to unlock all features'
                    }
                  </p>
                </div>
              </div>
              {isPremium && (
                <button 
                  onClick={handleCancel}
                  className="text-sm text-red-400 hover:text-red-300"
                  data-testid="cancel-subscription"
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        )}

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {Object.entries(TIERS).map(([tierKey, tier]) => {
            const TierIcon = tier.icon;
            const isCurrentTier = subscription?.tier === tierKey;
            const isBest = tierKey === 'premium';
            
            return (
              <div 
                key={tierKey}
                className={`glass-card p-8 relative overflow-hidden ${isBest ? 'border-purple-500/50' : ''}`}
                data-testid={`tier-${tierKey}`}
              >
                {isBest && (
                  <div className="absolute top-4 right-4 px-3 py-1 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full text-xs font-medium flex items-center gap-1">
                    <Star className="w-3 h-3" /> Best Value
                  </div>
                )}
                
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${tier.color} flex items-center justify-center mb-6`}>
                  <TierIcon className="w-7 h-7 text-white" />
                </div>
                
                <h2 className="text-2xl font-bold mb-2">{tier.name}</h2>
                <div className="flex items-baseline gap-1 mb-6">
                  <span className="text-4xl font-bold">${tier.price}</span>
                  {tier.price > 0 && <span className="text-gray-400">/month</span>}
                </div>
                
                <ul className="space-y-3 mb-8">
                  {tier.features.map((feature) => (
                    <li key={feature.text} className="flex items-center gap-3">
                      {feature.included ? (
                        <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                      ) : (
                        <Lock className="w-5 h-5 text-gray-500 flex-shrink-0" />
                      )}
                      <span className={feature.included ? 'text-gray-200' : 'text-gray-500'}>
                        {feature.text}
                      </span>
                    </li>
                  ))}
                </ul>
                
                <button
                  onClick={() => handleSubscribe(tierKey)}
                  disabled={checkoutLoading || (isCurrentTier && tierKey === 'premium')}
                  className={`w-full py-3 rounded-xl font-medium transition-all ${
                    isBest 
                      ? 'bg-gradient-to-r from-purple-500 to-pink-500 hover:opacity-90 text-white' 
                      : 'bg-white/10 hover:bg-white/20 text-white'
                  } ${(isCurrentTier && tierKey === 'premium') ? 'opacity-50 cursor-not-allowed' : ''}`}
                  data-testid={`subscribe-${tierKey}`}
                >
                  {(() => {
                    if (checkoutLoading) {
                      return (
                        <span className="flex items-center justify-center gap-2">
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Processing...
                        </span>
                      );
                    }
                    if (isCurrentTier && tierKey === 'premium') return 'Current Plan';
                    if (tierKey === 'free') return 'Get Started';
                    return (
                      <span className="flex items-center justify-center gap-2">
                        <Zap className="w-4 h-4" /> Upgrade Now
                      </span>
                    );
                  })()}
                </button>
              </div>
            );
          })}
        </div>

        {/* Features Highlight */}
        <div className="mt-16 grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          <div className="glass-card p-6 text-center">
            <Music className="w-10 h-10 text-purple-400 mx-auto mb-3" />
            <h3 className="font-semibold mb-2">Unlimited Streaming</h3>
            <p className="text-sm text-gray-400">Listen to all tracks without limits, anywhere, anytime</p>
          </div>
          <div className="glass-card p-6 text-center">
            <Download className="w-10 h-10 text-cyan-400 mx-auto mb-3" />
            <h3 className="font-semibold mb-2">Download All</h3>
            <p className="text-sm text-gray-400">Download any track for offline listening</p>
          </div>
          <div className="glass-card p-6 text-center">
            <Video className="w-10 h-10 text-pink-400 mx-auto mb-3" />
            <h3 className="font-semibold mb-2">Exclusive Content</h3>
            <p className="text-sm text-gray-400">Access premium videos, behind-the-scenes, and more</p>
          </div>
        </div>
      </div>
    </div>
  );
}
